import { useState, useEffect } from 'react';

/**
 * Hook do sprawdzania zgody Cookiebot na marketing
 * Czeka na załadowanie Cookiebot i nasłuchuje na zmiany zgody
 *
 * @param {number} maxWaitTime - Maksymalny czas oczekiwania w ms (domyślnie 5000ms)
 * @param {number} checkInterval - Interwał sprawdzania w ms (domyślnie 100ms)
 * @returns {{ hasConsent: boolean, isLoading: boolean }}
 */
const useCookiebotConsent = (maxWaitTime = 1500, checkInterval = 100) => {
    const [hasConsent, setHasConsent] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (typeof window === 'undefined') {
            setIsLoading(false);
            return;
        }

        let timeoutId;
        let intervalId;
        const startTime = Date.now();

        const checkCookiebot = () => {
            // Sprawdź czy Cookiebot jest załadowany i ma consent
            if (window.Cookiebot && window.Cookiebot.consent) {
                const marketing = window.Cookiebot.consent.marketing === true;
                setHasConsent(marketing);
                setIsLoading(false);
                clearInterval(intervalId);
                clearTimeout(timeoutId);
                return true;
            }

            // Timeout - przestań czekać po maxWaitTime
            if (Date.now() - startTime > maxWaitTime) {
                // Jeśli Cookiebot nie istnieje (dev/staging bez skryptu), pozwól na Quarticon
                // Na produkcji Cookiebot zawsze się załaduje przed timeoutem
                if (!window.Cookiebot) {
                    setHasConsent(true);
                }
                setIsLoading(false);
                clearInterval(intervalId);
                return true;
            }

            return false;
        };

        // Sprawdź od razu
        if (!checkCookiebot()) {
            // Jeśli nie gotowy, sprawdzaj co checkInterval
            intervalId = setInterval(checkCookiebot, checkInterval);
        }

        // Event listener dla zmian zgody Cookiebot
        const handleConsentChange = () => {
            if (window.Cookiebot && window.Cookiebot.consent) {
                setHasConsent(window.Cookiebot.consent.marketing === true);
                setIsLoading(false);
            }
        };

        // Nasłuchuj na eventy Cookiebot
        window.addEventListener('CookiebotOnAccept', handleConsentChange);
        window.addEventListener('CookiebotOnDecline', handleConsentChange);
        window.addEventListener('CookiebotOnLoad', handleConsentChange);

        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
            window.removeEventListener('CookiebotOnAccept', handleConsentChange);
            window.removeEventListener('CookiebotOnDecline', handleConsentChange);
            window.removeEventListener('CookiebotOnLoad', handleConsentChange);
        };
    }, [maxWaitTime, checkInterval]);

    return { hasConsent, isLoading };
};

export default useCookiebotConsent;
